// client/src/pages/Dashboard.js
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  getAllConversations,
  markConversationAsRead,
  handleArchiveConversations,
  markAllRead,
  createNewConversation,
  getNoAgentResponseStats
} from '../services/conversationService';
import { getAllUsers } from '../services/userService';
import { onBothSockets, getSockets } from '../services/socketService';
import { styledLog } from '../utils/functions';
import { getUserIdFromToken } from '../utils/auth';
import ConversationView from './ConversationView';
import LanguageSwitcher from '../components/LanguageSwitcher';
import notificationService from '../services/notificationService';

export default function Dashboard() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedTab, setSelectedTab] = useState(() => {
    return searchParams.get('conversationId') ? 'all' : 'active';
  }); // 'all' | 'unread' | 'archived' | 'active' | 'stale' | 'noResponse' | 'closed' | 'new'
  const [bulkMode, setBulkMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectedConversationId, setSelectedConversationId] = useState(() => {
    return searchParams.get('conversationId') || null;
  });
  const [whatsappIdFilter, setWhatsappIdFilter] = useState('');
  const [messageSearchFilter, setMessageSearchFilter] = useState('');
  const [agentIdFilter, setAgentIdFilter] = useState('');
  const [users, setUsers] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);
  const [creatingConversation, setCreatingConversation] = useState(false);
  const [noResponseStats, setNoResponseStats] = useState(null);
  const [showStats, setShowStats] = useState(false);
  const limit = 50;

  async function fetchConversations(reset = false) {
    try {
      if (reset) {
        setLoading(true);
        setOffset(0);
      } else {
        setLoadingMore(true);
      }

      const currentOffset = reset ? 0 : offset;
      const data = await getAllConversations({
        whatsappId: whatsappIdFilter || undefined,
        agentId: agentIdFilter || undefined,
        messageSearch: messageSearchFilter || undefined,
        showAll: selectedTab === 'all' ? true : undefined,
        noAgentResponse: selectedTab === 'noResponse' ? true : undefined,
        closed: selectedTab === 'closed' ? true : undefined,
        newConversations: selectedTab === 'new' ? true : undefined,
        limit,
        offset: currentOffset
      });

      if (reset) {
        setConversations(data.conversations);
      } else {
        setConversations(prev => [...prev, ...data.conversations]);
      }

      setTotal(data.total);
      setHasMore(currentOffset + data.conversations.length < data.total);
      setOffset(currentOffset + data.conversations.length);
    } catch (err) {
      console.error('[Dashboard] Error cargando conversaciones:', err);
      setError(t('dashboard.error.loading'));
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }

  async function fetchNoResponseStats() {
    try {
      const stats = await getNoAgentResponseStats();
      setNoResponseStats(stats);
    } catch (err) {
      console.error('[Dashboard] Error cargando estadísticas:', err);
    }
  }

  // Toggle modal visibility 2s after it appears
  useEffect(() => {
    if (success || error) {
      const timerSuccess = setTimeout(() => setSuccess(''), 2000);
      const timerError = setTimeout(() => setError(''), 2000);
      return () => {
        clearTimeout(timerSuccess);
        clearTimeout(timerError);
      };
    }
  }, [success, error]);

  // Cargar lista de usuarios al montar el componente
  useEffect(() => {
    async function loadUsers() {
      try {
        const data = await getAllUsers();
        setUsers(data.users || []);
      } catch (err) {
        console.error('[Dashboard] Error cargando usuarios:', err);
      }
    }
    loadUsers();
  }, []);

  // Clean conversationId from URL after consuming it
  useEffect(() => {
    if (searchParams.has('conversationId')) {
      searchParams.delete('conversationId');
      setSearchParams(searchParams, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchConversations(true);
    fetchNoResponseStats();

    const handleConversationUpdated = ({ conversationId, lastMessageAt, read, from, leadId, agentName, agentId, status, archived, noResponse }) => {
      styledLog(
        `[Dashboard] conversationUpdated: ${conversationId}, status=${status}, archived=${archived}, agentId=${agentId}`,
        `#9b08d7`
      );

      // Obtener el ID del usuario actual
      const currentUserId = getUserIdFromToken();

      // Mostrar notificación SOLO si:
      // 1. Es un mensaje nuevo del usuario y la pestaña no está enfocada
      // 2. Y (la conversación está asignada al usuario actual O no tiene agente asignado)
      if (read === false && !document.hasFocus()) {
        const isAssignedToMe = agentId && agentId === currentUserId;
        const hasNoAgent = !agentId;
        const shouldNotify = isAssignedToMe || hasNoAgent;

        console.log('[Dashboard] 🔔 Evaluando notificación:', {
          conversationId,
          agentId,
          currentUserId,
          isAssignedToMe,
          hasNoAgent,
          shouldNotify,
          noResponse,
          status
        });

        if (shouldNotify && (noResponse || status === 'agent_required')) {
          notificationService.notifyNewMessage(
            from || conversationId,
            status === 'agent_required' ? 'Requiere atención de agente' : 'Nuevo mensaje recibido',
            () => {
              // Al hacer click, abrir la conversación
              setSelectedConversationId(conversationId);
              setSelectedTab('active');
            }
          );
        }
      }

      setConversations(prev => {
        const exists = prev.some(conv => conv.id === conversationId);

        // Determinar si esta conversación debería estar visible según el tab actual
        const shouldBeVisible = () => {
          // Tab "archived": solo mostrar archivadas
          if (selectedTab === 'archived') {
            return archived === true;
          }
          // Tab "closed": solo mostrar cerradas
          if (selectedTab === 'closed') {
            return status === 'closed';
          }
          // Tab "new": solo mostrar nuevas
          if (selectedTab === 'new') {
            return status === 'new';
          }
          // Tab "active": mostrar no archivadas Y no cerradas Y no nuevas
          if (selectedTab === 'active') {
            return archived === false && status !== 'closed' && status !== 'new';
          }
          // Tab "unread": conversación debe estar no leída, no archivada, no cerrada y no nueva
          if (selectedTab === 'unread') {
            return !read && archived === false && status !== 'closed' && status !== 'new';
          }
          // Tab "noResponse" y otros: confiar en el backend, solo excluir cerradas y nuevas
          // (noResponse ya viene filtrado del backend)
          return status !== 'closed' && status !== 'new' && archived === false;
        };

        let newList;
        if (exists) {
          // Si existe, actualizar o eliminar según visibilidad
          if (shouldBeVisible()) {
            newList = prev.map(conv =>
              conv.id === conversationId
                ? {
                    ...conv,
                    lastMessageAt,
                    // Solo actualizar 'read' si viene definido en el evento
                    ...(read !== undefined && { read }),
                    status: status || 'automated',
                    agentName: agentName || null,
                    leadId: leadId || null,
                    archived: !!archived,
                    ...(noResponse !== undefined && { noResponse })
                  }
                : conv
            );
          } else {
            // Eliminar de la lista si ya no debería ser visible
            newList = prev.filter(conv => conv.id !== conversationId);
          }
        } else {
          // Si no existe, agregar solo si debería ser visible
          if (shouldBeVisible()) {
            newList = [
              ...prev,
              {
                id: conversationId,
                whatsappId: from,
                lastMessageAt,
                read: read !== undefined ? read : false,
                status: status || 'automated',
                agentName: agentName || null,
                leadId: leadId || null,
                archived: !!archived,
                noResponse: noResponse !== undefined ? noResponse : false
              }
            ];
          } else {
            // No agregar, mantener lista actual
            newList = prev;
          }
        }
        return newList.sort((a, b) => new Date(b.lastMessageAt) - new Date(a.lastMessageAt));
      });
    };

    // Handler para cuando este usuario marca una conversación como leída
    const handleConversationMarkedRead = ({ conversationId }) => {
      styledLog(
        `[Dashboard] conversationMarkedRead: ${conversationId}`,
        `#52c41a`
      );

      setConversations(prev => {
        return prev.map(conv =>
          conv.id === conversationId
            ? { ...conv, read: true }
            : conv
        );
      });
    };

    // Conectarse a ambos backends para recibir eventos de IT y ES
    const sockets = getSockets();

    // Registrar listeners en ambos sockets
    onBothSockets('conversationUpdated', handleConversationUpdated);
    onBothSockets('conversationMarkedRead', handleConversationMarkedRead);

    return () => {
      // Limpiar listeners de ambos sockets
      sockets.it.off('conversationUpdated', handleConversationUpdated);
      sockets.it.off('conversationMarkedRead', handleConversationMarkedRead);
      sockets.es.off('conversationUpdated', handleConversationUpdated);
      sockets.es.off('conversationMarkedRead', handleConversationMarkedRead);
    };
  }, [whatsappIdFilter, agentIdFilter, selectedTab]);

  // Detectar scroll y cargar más
  const handleScroll = (e) => {
    const bottom = e.target.scrollHeight - e.target.scrollTop <= e.target.clientHeight + 100;
    if (bottom && !loadingMore && hasMore) {
      fetchConversations(false);
    }
  };

  const handleClickConversation = async id => {
    if (bulkMode) {
      // toggle selection
      setSelectedIds(prev =>
        prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
      );
      return;
    }
    try {
      await markConversationAsRead(id);
    } catch (err) {
      console.error('[Dashboard] Error marcando leída:', err);
    } finally {
      setSelectedConversationId(id);
    }
  };

  const handleToggleBulkMode = () => {
    setBulkMode(!bulkMode);
    setSelectedIds([]);
  };

  const handleApplyBulk = async () => {
    if (!selectedIds.length) return;
    try {
      if (selectedTab === 'archived') {
        await handleArchiveConversations(false, selectedIds);
        setSuccess(t('common.success'));
      } else {
        await handleArchiveConversations(true, selectedIds);
        setSuccess(t('common.success'));
      }
      fetchConversations(true);
      setSelectedIds([]);
      setBulkMode(false);
    } catch (err) {
      console.error('[Dashboard] Error aplicando acción bulk:', err);
      setError(t('dashboard.error.archive'));
    }
  };

  const handleFilterChange = (e) => {
    setWhatsappIdFilter(e.target.value);
  };

  const handleFilterSubmit = (e) => {
    e.preventDefault();
    fetchConversations(true);
  };

  const filteredConversations = conversations.filter(conv => {
    if (selectedTab === 'all') return true; // Mostrar todas
    if (selectedTab === 'unread') return !conv.read;
    if (selectedTab === 'archived') return conv.archived === true;
    if (selectedTab === 'active') return conv.archived === false;
    if (selectedTab === 'noResponse') return true; // Ya filtrado en el backend
    if (selectedTab === 'closed') return true; // Ya filtrado en el backend
    if (selectedTab === 'new') return true; // Ya filtrado en el backend
    return true;
  });

  async function handleMarkAllRead() {
    try {
      await markAllRead();
      // localmente aseguramos que todas estén en estado leído
      setConversations(prev =>
        prev.map(c => ({ ...c, read: true }))
      );
    } catch (err) {
      console.error(err);
      setError(t('dashboard.error.markRead'));
    }
  }

  async function handleCreateConversation() {
    if (!whatsappIdFilter || !whatsappIdFilter.trim()) {
      setError('Por favor ingresa un número de teléfono válido');
      return;
    }

    try {
      setCreatingConversation(true);
      const result = await createNewConversation(whatsappIdFilter.trim());

      setSuccess('Conversación creada exitosamente');

      // Limpiar el filtro y recargar
      setWhatsappIdFilter('');
      await fetchConversations(true);

      // Seleccionar la nueva conversación
      setSelectedConversationId(result.conversation.id);
    } catch (err) {
      console.error('[Dashboard] Error creando conversación:', err);

      // Si ya existe, intentar encontrarla y seleccionarla
      if (err.message.includes('Ya existe')) {
        await fetchConversations(true);
        const existing = conversations.find(c => c.whatsappId === whatsappIdFilter.trim());
        if (existing) {
          setSelectedConversationId(existing.id);
          setSuccess('Conversación encontrada');
        }
      } else {
        setError(err.message || 'Error al crear conversación');
      }
    } finally {
      setCreatingConversation(false);
    }
  }

  function highlightMatch(text, term) {
    if (!term || !text) return text;
    const lower = text.toLowerCase();
    const idx = lower.indexOf(term.toLowerCase());
    if (idx === -1) return text.length > 80 ? text.substring(0, 80) + '...' : text;

    const start = Math.max(0, idx - 20);
    const end = Math.min(text.length, idx + term.length + 40);
    const before = (start > 0 ? '...' : '') + text.substring(start, idx);
    const match = text.substring(idx, idx + term.length);
    const after = text.substring(idx + term.length, end) + (end < text.length ? '...' : '');

    return <>{before}<mark style={{ backgroundColor: '#ffd54f', padding: '0 2px', borderRadius: '2px' }}>{match}</mark>{after}</>;
  }

  return (
    <div style={styles.appContainer}>
        {/* Columna izquierda: filtros  lista */}
        <div style={styles.leftPane}>
        <div style={styles.leftHeader}>

          {/* Filtro por whatsappId */}
          <form onSubmit={handleFilterSubmit} style={styles.filterForm}>
            <input
              type="text"
              placeholder={t('dashboard.filter.placeholder')}
              value={whatsappIdFilter}
              onChange={handleFilterChange}
              style={styles.filterInput}
            />
            {whatsappIdFilter && (
              <button
                type="button"
                onClick={() => {
                  setWhatsappIdFilter('');
                  setTimeout(() => fetchConversations(true), 0);
                }}
                style={styles.clearBtn}
              >
                ✕
              </button>
            )}
          </form>

          {/* Filtro por mensaje */}
          <form onSubmit={handleFilterSubmit} style={styles.filterForm}>
            <input
              type="text"
              placeholder="Buscar por mensaje..."
              value={messageSearchFilter}
              onChange={(e) => setMessageSearchFilter(e.target.value)}
              style={styles.filterInput}
            />
            {messageSearchFilter && (
              <button
                type="button"
                onClick={() => {
                  setMessageSearchFilter('');
                  setTimeout(() => fetchConversations(true), 0);
                }}
                style={styles.clearBtn}
              >
                ✕
              </button>
            )}
          </form>

          {/* Filtro por agente */}
          <div style={styles.filterForm}>
            <select
              value={agentIdFilter}
              onChange={(e) => setAgentIdFilter(e.target.value)}
              style={styles.agentSelect}
            >
              <option value="">Todos los agentes</option>
              <option value="unassigned">Sin asignar</option>
              {users.map(user => (
                <option key={user.id} value={user.id}>
                  {user.name}
                </option>
              ))}
            </select>
            {agentIdFilter && (
              <button
                type="button"
                onClick={() => setAgentIdFilter('')}
                style={styles.clearBtn}
              >
                ✕
              </button>
            )}
          </div>

          <div style={styles.tabContainer}>
            <button
              style={{
                ...styles.tab,
                ...(selectedTab === 'all' ? styles.activeTab : {})
              }}
              onClick={() => setSelectedTab('all')}
              onMouseEnter={(e) => {
                if (selectedTab !== 'all') {
                  e.currentTarget.style.borderColor = '#1A6EFF';
                  e.currentTarget.style.background = '#F0F5FF';
                }
              }}
              onMouseLeave={(e) => {
                if (selectedTab !== 'all') {
                  e.currentTarget.style.borderColor = 'transparent';
                  e.currentTarget.style.background = '#fff';
                }
              }}
            >
              📋 Todas
            </button>
            <button
              style={{
                ...styles.tab,
                ...(selectedTab === 'active' ? styles.activeTab : {})
              }}
              onClick={() => setSelectedTab('active')}
              onMouseEnter={(e) => {
                if (selectedTab !== 'active') {
                  e.currentTarget.style.borderColor = '#1A6EFF';
                  e.currentTarget.style.background = '#F0F5FF';
                }
              }}
              onMouseLeave={(e) => {
                if (selectedTab !== 'active') {
                  e.currentTarget.style.borderColor = 'transparent';
                  e.currentTarget.style.background = '#fff';
                }
              }}
            >
              ✅ {t('dashboard.tabs.active')}
            </button>
            <button
              style={{
                ...styles.tab,
                ...(selectedTab === 'unread' ? styles.activeTab : {})
              }}
              onClick={() => setSelectedTab('unread')}
              onMouseEnter={(e) => {
                if (selectedTab !== 'unread') {
                  e.currentTarget.style.borderColor = '#1A6EFF';
                  e.currentTarget.style.background = '#F0F5FF';
                }
              }}
              onMouseLeave={(e) => {
                if (selectedTab !== 'unread') {
                  e.currentTarget.style.borderColor = 'transparent';
                  e.currentTarget.style.background = '#fff';
                }
              }}
            >
              🔵 {t('dashboard.tabs.unread')}
            </button>
            <button
              style={{
                ...styles.tab,
                ...(selectedTab === 'noResponse' ? styles.activeTab : {})
              }}
              onClick={() => setSelectedTab('noResponse')}
              onMouseEnter={(e) => {
                if (selectedTab !== 'noResponse') {
                  e.currentTarget.style.borderColor = '#1A6EFF';
                  e.currentTarget.style.background = '#F0F5FF';
                }
              }}
              onMouseLeave={(e) => {
                if (selectedTab !== 'noResponse') {
                  e.currentTarget.style.borderColor = 'transparent';
                  e.currentTarget.style.background = '#fff';
                }
              }}
            >
              ⚠️ Sin Respuesta {noResponseStats?.total > 0 && `(${noResponseStats.total})`}
            </button>
            <button
              style={{
                ...styles.tab,
                ...(selectedTab === 'new' ? styles.activeTab : {})
              }}
              onClick={() => setSelectedTab('new')}
              onMouseEnter={(e) => {
                if (selectedTab !== 'new') {
                  e.currentTarget.style.borderColor = '#1A6EFF';
                  e.currentTarget.style.background = '#F0F5FF';
                }
              }}
              onMouseLeave={(e) => {
                if (selectedTab !== 'new') {
                  e.currentTarget.style.borderColor = 'transparent';
                  e.currentTarget.style.background = '#fff';
                }
              }}
            >
              🆕 Nuevas
            </button>
            <button
              style={{
                ...styles.tab,
                ...(selectedTab === 'closed' ? styles.activeTab : {})
              }}
              onClick={() => setSelectedTab('closed')}
              onMouseEnter={(e) => {
                if (selectedTab !== 'closed') {
                  e.currentTarget.style.borderColor = '#1A6EFF';
                  e.currentTarget.style.background = '#F0F5FF';
                }
              }}
              onMouseLeave={(e) => {
                if (selectedTab !== 'closed') {
                  e.currentTarget.style.borderColor = 'transparent';
                  e.currentTarget.style.background = '#fff';
                }
              }}
            >
              🔒 Cerradas
            </button>
            <button
              style={{
                ...styles.tab,
                ...(selectedTab === 'archived' ? styles.activeTab : {})
              }}
              onClick={() => setSelectedTab('archived')}
              onMouseEnter={(e) => {
                if (selectedTab !== 'archived') {
                  e.currentTarget.style.borderColor = '#1A6EFF';
                  e.currentTarget.style.background = '#F0F5FF';
                }
              }}
              onMouseLeave={(e) => {
                if (selectedTab !== 'archived') {
                  e.currentTarget.style.borderColor = 'transparent';
                  e.currentTarget.style.background = '#fff';
                }
              }}
            >
              📦 {t('dashboard.tabs.archived')}
            </button>
          </div>
          <div style={styles.actionsRow}>
            <button onClick={handleMarkAllRead} style={styles.actionBtn}>{t('dashboard.bulkMode.markRead')}</button>
            <button onClick={() => setShowStats(!showStats)} style={styles.actionBtn}>
              {showStats ? 'Ocultar Stats' : 'Ver Stats'}
            </button>
            <button onClick={handleToggleBulkMode} style={styles.actionBtn}>
              {bulkMode ? t('dashboard.bulkMode.disable') : selectedTab === 'archived' ? t('dashboard.bulkMode.unarchive') : t('dashboard.bulkMode.archive')}
            </button>
            {bulkMode && (
              <button onClick={handleApplyBulk}
                disabled={!selectedIds.length}
                style={{ ...styles.actionBtn, opacity: selectedIds.length ? 1 : 0.5 }}>
                {selectedTab === 'archived' ? t('dashboard.bulkMode.unarchive') + ' ' + t('dashboard.bulkMode.selected') : t('dashboard.bulkMode.archive') + ' ' + t('dashboard.bulkMode.selected')}
              </button>
            )}
          </div>
          {/* Mostrar total de conversaciones */}
          <div style={styles.statsRow}>
            {`${t('dashboard.conversations.showing')} ${filteredConversations.length} ${t('dashboard.conversations.from')} ${total} ${t('dashboard.conversations.conversations')}`} 
          </div>
        </div>

        <div style={styles.listContainer} onScroll={handleScroll}>
          {filteredConversations.length === 0 && !loading && !error ? (
            <div style={styles.emptyMessageBox}>
              <p style={styles.emptyMessageText}>
                {whatsappIdFilter
                  ? 'No se encontraron conversaciones con este número'
                  : t(`dashboard.empty.${selectedTab}`)}
              </p>
              {whatsappIdFilter && (
                <button
                  onClick={handleCreateConversation}
                  disabled={creatingConversation}
                  style={styles.createConversationBtn}
                >
                  {creatingConversation
                    ? 'Creando...'
                    : `Crear nueva conversación con ${whatsappIdFilter}`}
                </button>
              )}
            </div>
          ) : (
            filteredConversations.map(conv => {
              const isUnread = !conv.read;
              const isAutomated = conv.status === 'automated';
              const agentRequired = conv.status === 'agent_required';
              const agentAssigned = conv.status === 'agent_assigned';
              const isBroker = conv.type === 'broker';
              const checked = selectedIds.includes(conv.id);
              const isSelected = selectedConversationId === conv.id;
              return (
                <div key={conv.id}
                  style={{
                    ...styles.rowCard,
                    backgroundColor: isSelected ? '#e6f7ff'
                      : isBroker ? '#fff7e6'
                        : (isUnread && !agentRequired) ? '#fffbe6'
                          : agentRequired ? '#ffdfcd' : '#fff',
                    borderColor: isSelected ? '#423ef4'
                      : isBroker ? '#fa8c16'
                        : isAutomated ? '#91d5ff'
                          : agentRequired ? '#ff122e' : '#ddd',
                    borderLeftWidth: isBroker ? '4px' : '1px',
                  }}
                  onClick={() => handleClickConversation(conv.id)}>
                  {bulkMode && (
                    <input type="checkbox"
                      checked={checked}
                      onChange={() => handleClickConversation(conv.id)}
                      style={styles.checkbox} />
                  )}
                  <div style={styles.rowMain}>
                    <div style={styles.titleLine}>
                      <strong>{conv.whatsappId}</strong>
                      {isBroker && (
                        <span style={styles.brokerBadge} title={conv.brokerInfo?.matchedVia === 'broker_employee' ? 'Empleado de broker' : 'Broker'}>
                          🏷️ BROKER
                        </span>
                      )}
                    </div>
                    {isBroker && conv.brokerInfo?.name && (
                      <div style={styles.brokerName}>{conv.brokerInfo.name}</div>
                    )}
                    <div style={styles.metaLine}>
                      <span>{t(`dashboard.conversation.${conv.status}`) || conv.status}</span>
                      {conv.leadId && (
                      <div style={styles.leadIdContainer}>
                        <strong>{t('dashboard.conversation.lead')} ID:</strong>
                        <span style={{ marginLeft: '4px' }}>{conv.leadId}</span>
                        <button
                          onClick={e => {
                            e.stopPropagation();
                            navigator.clipboard.writeText(conv.leadId);
                            setSuccess(t('common.success'));
                          }}
                          style={styles.copyButton}
                          title={t('common.copy')}
                        >
                          📋
                        </button>
                      </div>
                    )}
                    </div>
                    {conv.matchedMessage && (
                      <div style={styles.matchedSnippet}>
                        <span style={{ fontSize: '0.75rem', flexShrink: 0 }}>
                          {conv.matchedMessage.from === 'user' ? '👤' : '🤖'}
                        </span>
                        <span style={styles.matchedSnippetText}>
                          {highlightMatch(conv.matchedMessage.body, messageSearchFilter)}
                        </span>
                      </div>
                    )}
                    <div style={styles.timeLine}>
                      {new Date(conv.lastMessageAt).toLocaleString()}
                    </div>
                  </div>
                  {!bulkMode && conv.agentName && (
                    <div style={styles.botIcon}>🗣️ {conv.agentName}</div>
                  )}
                  {!bulkMode && !conv.agentName && isAutomated && (
                    <div style={styles.botIcon} title={t('dashboard.conversation.automated')}>🤖</div>
                  )}
                  {!bulkMode && isUnread && (
                    <span style={styles.unreadBadge}>{t('dashboard.conversation.unread')}</span>
                  )}
                  {!bulkMode && conv.noResponse && (
                    <span style={styles.noResponseBadge}>⏳ Sin respuesta</span>
                  )}
                </div>
              );
            })
          )}
          {loadingMore && (
            <div style={styles.loadingMore}>
              <p>Cargando más conversaciones...</p>
            </div>
          )}
        </div>
      </div>

      {/* Columna derecha: conversación seleccionada */}
      <div style={styles.rightPane}>
        {selectedConversationId ? (
          <ConversationView conversationId={selectedConversationId} />
        ) : (
          <div style={styles.placeholder}>
            <h3>{t('common.selectConversation')}</h3>
            <p>{t('common.selectConversationDesc')}</p>
          </div>
        )}
      </div>

      {loading && (<div style={styles.modal}><p>{t('common.loading')}</p></div>)}
      {error && (<div style={{ ...styles.modal, borderColor: '#f5222d' }}>
        <p style={{ margin: 0, color: '#f5222d' }}>{t('common.error')}: {error}</p>
      </div>)}
      {success && (<div style={{ ...styles.modal, borderColor: '#00B049' }}>
        <p style={{ margin: 0, color: '#00B049' }}>{success}</p>
      </div>)}

      {/* Modal de estadísticas */}
      {showStats && noResponseStats && (
        <div style={styles.statsModal}>
          <div style={styles.statsModalContent}>
            <div style={styles.statsModalHeader}>
              <h3>Estadísticas - Conversaciones Sin Respuesta</h3>
              <button onClick={() => setShowStats(false)} style={styles.statsCloseBtn}>✕</button>
            </div>

            <div style={styles.statsGrid}>
              <div style={styles.statsCard}>
                <h4>Total</h4>
                <p style={styles.statsNumber}>{noResponseStats.total}</p>
              </div>

              <div style={styles.statsCard}>
                <h4>Por Estado</h4>
                {Object.entries(noResponseStats.byStatus || {}).map(([status, count]) => (
                  <div key={status} style={styles.statLine}>
                    <span>{t(`dashboard.conversation.${status}`) || status}:</span>
                    <strong>{count}</strong>
                  </div>
                ))}
              </div>

              <div style={styles.statsCard}>
                <h4>Por Mercado</h4>
                {Object.entries(noResponseStats.byMarket || {}).map(([market, count]) => (
                  <div key={market} style={styles.statLine}>
                    <span>{market.toUpperCase()}:</span>
                    <strong>{count}</strong>
                  </div>
                ))}
              </div>
            </div>

            <button
              onClick={() => {
                setShowStats(false);
                setSelectedTab('noResponse');
              }}
              style={styles.viewAllBtn}
            >
              Ver Todas las Conversaciones Sin Respuesta
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  sidebar: {
    width: '200px', backgroundColor: '#2c3e50', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '1rem'
  },
  archiveButton: {
    padding: '0.5rem 1rem', backgroundColor: '#423ef4', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', width: '100%', marginBottom: '0.5rem'
  },
  logoutButton: {
    padding: '0.5rem 1rem', backgroundColor: '#e74c3c', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', width: '100%'
  },
  mainContainer: {
    flex: 1, padding: '1rem', backgroundColor: '#fafafa', overflowY: 'auto', display: 'flex', flexDirection: 'column'
  },
  title: { textAlign: 'center', marginBottom: '1rem' },
  tabContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.5rem',
    marginBottom: '1rem',
    padding: '0.5rem',
    backgroundColor: '#f8f9fa',
    borderRadius: '12px'
  },
  tab: {
    padding: '8px 16px',
    border: '2px solid transparent',
    background: '#fff',
    cursor: 'pointer',
    borderRadius: '8px',
    fontSize: '0.9rem',
    fontWeight: '500',
    transition: 'all 0.2s ease',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    display: 'flex',
    alignItems: 'center',
    gap: '6px'
  },
  activeTab: {
    background: '#1A6EFF',
    color: '#fff',
    borderColor: '#1A6EFF',
    fontWeight: '600',
    boxShadow: '0 2px 6px rgba(26, 110, 255, 0.3)',
    transform: 'translateY(-1px)'
  },
  emptyContainer: { flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' },
  emptyMessageBox: { padding: '2rem', backgroundColor: '#fff', border: '1px solid #ddd', borderRadius: '4px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' },
  emptyMessageText: { margin: 0, color: '#555', fontSize: '1rem', textAlign: 'center' },
  gridContainer: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem' },
  card: { position: 'relative', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', padding: '0.75rem 1rem', border: '1px solid #ddd', borderRadius: '4px', backgroundColor: '#fff', transition: 'box-shadow 0.2s', minHeight: '120px' },
  checkbox: { position: 'absolute', top: '8px', right: '8px', width: '18px', height: '18px' },
  info: { flex: 1 },
  botIcon: { marginTop: '0.5rem', fontSize: '1.5rem', color: '#423ef4', alignSelf: 'flex-end' },
  unreadBadge: { position: 'absolute', top: '8px', right: '8px', backgroundColor: '#f5222d', color: '#fff', fontSize: '0.75rem', padding: '2px 6px', borderRadius: '4px' },
  noResponseBadge: { position: 'absolute', top: '8px', right: '80px', backgroundColor: '#ff9800', color: '#fff', fontSize: '0.75rem', padding: '2px 6px', borderRadius: '4px', fontWeight: 'bold' },
  modal: { position: 'fixed', top: '1rem', right: '1rem', backgroundColor: '#fff', border: '1px solid #ddd', borderRadius: '4px', padding: '1rem', boxShadow: '0 2px 8px rgba(0,0,0,0.15)', zIndex: 1000 },
  leadIdContainer: { display: 'flex', alignItems: 'center', marginBottom: '0.25rem' },
  copyButton: {
    marginLeft: '8px', padding: '2px 6px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '1rem'
  },
  appContainer: { display: 'flex', height: '100%', width: '100%', position: 'relative', background: '#fafafa' },
  leftPane: { width: '520px', display: 'flex', flexDirection: 'column', borderRight: '1px solid #e8e8e8', background: '#fff' },
  leftHeader: { padding: '0.75rem 0.75rem 0', position: 'sticky', top: 0, background: '#fff', zIndex: 1, borderBottom: '1px solid #f0f0f0' },
  headerTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' },
  actionsRow: { display: 'flex', gap: '0.5rem', padding: '0.5rem 0.75rem', justifyContent: 'flex-end' },
  actionBtn: { padding: '0.5rem 0.75rem', background: '#423ef4', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' },
  listContainer: { overflowY: 'auto', padding: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' },
  rowCard: { position: 'relative', display: 'flex', gap: '0.5rem', padding: '0.5rem 0.75rem', border: '1px solid #ddd', borderRadius: 6, cursor: 'pointer', alignItems: 'center' },
  rowMain: { display: 'flex', flexDirection: 'column', gap: 2, flex: 1 },
  titleLine: { display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' },
  brokerBadge: { fontSize: '0.7rem', fontWeight: 700, padding: '2px 6px', backgroundColor: '#fa8c16', color: '#fff', borderRadius: '4px', letterSpacing: '0.5px' },
  brokerName: { fontSize: '0.8rem', color: '#ad6800', fontStyle: 'italic' },
  metaLine: { fontSize: '0.85rem', color: '#666' },
  timeLine: { fontSize: '0.8rem', color: '#999' },
  matchedSnippet: { display: 'flex', alignItems: 'flex-start', gap: '4px', padding: '4px 6px', backgroundColor: '#f5f5f5', borderRadius: '4px', marginTop: '4px', fontSize: '0.8rem', color: '#555', lineHeight: '1.3', maxHeight: '2.6em', overflow: 'hidden' },
  matchedSnippetText: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 },
  rightPane: { flex: 1, minWidth: 0, display: 'flex' },
  placeholder: { margin: 'auto', textAlign: 'center', color: '#777' },
  filterForm: { display: 'flex', gap: '0.5rem', padding: '0.5rem 0.75rem', alignItems: 'center' },
  filterInput: { flex: 1, padding: '0.5rem', border: '1px solid #ddd', borderRadius: 4, fontSize: '0.9rem' },
  filterBtn: { padding: '0.5rem 0.75rem', background: '#423ef4', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' },
  clearBtn: { padding: '0.5rem 0.75rem', background: '#f5222d', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' },
  statsRow: { padding: '0.5rem 0.75rem', fontSize: '0.85rem', color: '#666', textAlign: 'center', borderTop: '1px solid #f0f0f0' },
  loadingMore: { padding: '1rem', textAlign: 'center', color: '#999', fontSize: '0.9rem' },
  createConversationBtn: {
    marginTop: '1rem',
    padding: '0.75rem 1.5rem',
    backgroundColor: '#52c41a',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.95rem',
    fontWeight: '500',
    transition: 'background-color 0.3s',
  },
  agentSelect: {
    flex: 1,
    padding: '0.5rem',
    border: '1px solid #ddd',
    borderRadius: 4,
    fontSize: '0.9rem',
    backgroundColor: '#fff',
    cursor: 'pointer',
  },
  statsModal: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 2000,
  },
  statsModalContent: {
    backgroundColor: '#fff',
    borderRadius: '8px',
    padding: '2rem',
    maxWidth: '800px',
    width: '90%',
    maxHeight: '80vh',
    overflowY: 'auto',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
  },
  statsModalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1.5rem',
    borderBottom: '2px solid #f0f0f0',
    paddingBottom: '1rem',
  },
  statsCloseBtn: {
    background: 'none',
    border: 'none',
    fontSize: '1.5rem',
    cursor: 'pointer',
    color: '#999',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '1rem',
    marginBottom: '1.5rem',
  },
  statsCard: {
    padding: '1rem',
    backgroundColor: '#f9f9f9',
    borderRadius: '6px',
    border: '1px solid #e8e8e8',
  },
  statsNumber: {
    fontSize: '2.5rem',
    fontWeight: 'bold',
    color: '#423ef4',
    margin: '0.5rem 0',
  },
  statLine: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '0.5rem 0',
    borderBottom: '1px solid #e8e8e8',
  },
  viewAllBtn: {
    width: '100%',
    padding: '0.75rem',
    backgroundColor: '#423ef4',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '1rem',
    fontWeight: '500',
  },
};
